import { useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';

// ============================================================
// useMagnetic — sutil "atração" do botão na proximidade do cursor.
// ============================================================
// Aplicado em CTAs primários do hero. Desativa automaticamente em:
//   - touch devices (sem mousemove)
//   - prefers-reduced-motion: reduce
//
// Uso:
//   const ref = useMagnetic<HTMLButtonElement>({ strength: 18 });
//   <button ref={ref}>...</button>
// ============================================================

interface UseMagneticOptions {
  /** Distância máxima de translação em px (default: 14). */
  strength?: number;
  /** Raio em px ao redor do botão para começar a "atrair" (default: 80). */
  radius?: number;
}

export function useMagnetic<T extends HTMLElement = HTMLElement>({
  strength = 14,
  radius = 80,
}: UseMagneticOptions = {}) {
  const ref = useRef<T | null>(null);
  const reduced = useReducedMotion();

  const onMove = useCallback(
    (e: PointerEvent) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const reach = Math.max(rect.width, rect.height) / 2 + radius;
      if (dist > reach) {
        el.style.transform = '';
        return;
      }
      const factor = (1 - dist / reach) * strength;
      el.style.transform = `translate3d(${(dx / reach) * factor}px, ${(dy / reach) * factor}px, 0)`;
    },
    [radius, strength],
  );

  const onLeave = useCallback(() => {
    const el = ref.current;
    if (el) el.style.transform = '';
  }, []);

  useEffect(() => {
    if (reduced) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, [onMove, onLeave, reduced]);

  return ref;
}
